import React, { useState, useEffect } from 'react';
import { Modal, Radio, Input, Form, message } from 'antd';
import { useTranslation } from 'react-i18next';

interface ReviewModalProps {
    visible: boolean;
    driverAccountId: number | null;
    reviewResult?: 'APPROVED' | 'REJECTED'; // 可选的预选审核结果
    onCancel: () => void;
    onSubmit: (reviewResult: 'APPROVED' | 'REJECTED', reviewComment?: string) => void; // 修改：同时接收reviewResult和reviewComment
    loading?: boolean;
}

const ReviewModal: React.FC<ReviewModalProps> = ({
    visible,
    driverAccountId,
    reviewResult: propReviewResult,
    onCancel,
    onSubmit,
    loading = false,
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [reviewResult, setReviewResult] = useState<'APPROVED' | 'REJECTED' | undefined>(propReviewResult);
    
    // 当propReviewResult变化时，更新内部状态
    useEffect(() => {
        if (propReviewResult) {
            setReviewResult(propReviewResult);
        }
    }, [propReviewResult]);
    
    // 当弹窗打开时，如果有预选的审核结果，直接设置
    useEffect(() => {
        if (visible && propReviewResult) {
            setReviewResult(propReviewResult);
        } else if (!visible) {
            form.resetFields();
            if (!propReviewResult) {
                setReviewResult(undefined);
            }
        }
    }, [visible, propReviewResult, form]);
    
    const handleOk = () => {
        const finalResult = reviewResult || propReviewResult;
        if (!finalResult) {
            message.warning(t('riskControl.driverVerification.messages.selectReviewResult'));
            return;
        }
        
        form.validateFields().then((values) => {
            onSubmit(finalResult, values.reviewComment);
            form.resetFields();
            if (!propReviewResult) {
                setReviewResult(undefined);
            }
        });
    };
    
    const handleCancel = () => {
        form.resetFields();
        if (!propReviewResult) {
            setReviewResult(undefined);
        }
        onCancel();
    };
    
    return (
        <Modal
            title={t('riskControl.driverVerification.reviewModal.title') as string}
            open={visible}
            onOk={handleOk}
            onCancel={handleCancel}
            confirmLoading={loading}
            okText={t('riskControl.driverVerification.reviewModal.confirm') as string}
            cancelText={t('riskControl.driverVerification.reviewModal.cancel') as string}
        >
            <Form form={form} layout="vertical">
                {!propReviewResult && (
                    <Form.Item
                        label={t('riskControl.driverVerification.reviewModal.reviewResult') as string}
                        required
                    >
                        <Radio.Group
                            value={reviewResult}
                            onChange={(e) => setReviewResult(e.target.value)}
                        >
                            <Radio value="APPROVED">
                                {t('riskControl.driverVerification.reviewModal.approve') as string}
                            </Radio>
                            <Radio value="REJECTED">
                                {t('riskControl.driverVerification.reviewModal.reject') as string}
                            </Radio>
                        </Radio.Group>
                    </Form.Item>
                )}
                
                {propReviewResult && (
                    <div className="mb-4">
                        <span className="text-gray-600">{t('riskControl.driverVerification.reviewModal.reviewResult')}: </span>
                        <span className="font-semibold">
                            {propReviewResult === 'APPROVED' 
                                ? t('riskControl.driverVerification.reviewModal.approve')
                                : t('riskControl.driverVerification.reviewModal.reject')}
                        </span>
                    </div>
                )}
                
                <Form.Item
                    label={t('riskControl.driverVerification.reviewModal.comment') as string}
                    name="reviewComment"
                >
                    <Input.TextArea
                        rows={4}
                        placeholder={t('riskControl.driverVerification.reviewModal.comment') as string}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ReviewModal;

